import React from "react";
import {
  Box,
  Drawer,
  List,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import SecurityIcon from "@mui/icons-material/Security";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SpeedIcon from "@mui/icons-material/Speed";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ListIcon from "@mui/icons-material/List";
import TableChartIcon from "@mui/icons-material/TableChart";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { FaYoutube, FaSpotify } from "react-icons/fa";
import SidebarItem, { NavItemChild } from "./SidebarItem";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../../assets/Logo";
import { themeColors } from "../../utils/theme";

interface SidebarProps {
  expanded: boolean;
  onToggle: () => void;
}

const MAILER_CHILDREN: NavItemChild[] = [
  {
    text: "Send",
    path: "/mailer/template",
    icon: <SendIcon sx={{ fontSize: 18 }} />,
  },
  {
    text: "Reports",
    path: "/mailer/reports",
    icon: <AssessmentIcon sx={{ fontSize: 18 }} />,
  },
  {
    text: "Logs",
    path: "/mailer/logs",
    icon: <ListIcon sx={{ fontSize: 18 }} />,
  },
];

const OUTREACH_CHILDREN: NavItemChild[] = [
  {
    text: "Tables",
    icon: <TableChartIcon />,
    children: [
      {
        text: "YouTube Reaction",
        path: "/outreach/youtube-reaction",
        icon: <FaYoutube />,
      },
      {
        text: "YouTube Playlisting",
        path: "/outreach/youtube-playlisting",
        icon: <FaYoutube />,
      },
      {
        text: "Outreach/Pending",
        path: "/outreach/pending",
        icon: <AccessTimeIcon sx={{ fontSize: 18 }} />,
      },
      {
        text: "Spotify Playlists",
        path: "/outreach/spotify",
        icon: <FaSpotify />,
      },
      {
        text: "Music Blogs",
        path: "/outreach/blogs",
      },
    ],
  },
  {
    text: "Campaigns",
    path: "/outreach/campaigns",
  },
  {
    text: "Analytics",
    path: "/outreach/analytics",
  },
];

const Sidebar: React.FC<SidebarProps> = ({ expanded, onToggle }) => {
  const theme = useTheme();
  const { currentUser, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  return (
    <Drawer
      variant="permanent"
      open={expanded}
      sx={{
        width: expanded ? 240 : 64,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: expanded ? 240 : 64,
          overflowX: "hidden",
          backgroundColor: themeColors.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          padding: theme.spacing(2, 1),
          minHeight: 64,
        }}
      >
        {expanded && (
          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
            <Logo width={30} height={30} />
            <Link
              to="/dashboard"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <Typography
                variant="h6"
                noWrap
                sx={{
                  ml: 1,
                  fontWeight: 500,
                  color: "white",
                }}
              >
                Distro Nation
              </Typography>
            </Link>
          </Box>
        )}
        {!expanded && <Logo width={30} height={30} />}
        <IconButton onClick={onToggle}>
          {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ mt: 1 }}>
        <SidebarItem
          icon={<EmailIcon />}
          text="Mailer"
          expanded={expanded}
          children={MAILER_CHILDREN}
        />
        <SidebarItem
          icon={<CampaignIcon />}
          text="Outreach"
          expanded={expanded}
          children={OUTREACH_CHILDREN}
        />
        {isAdmin() && (
          <>
            <SidebarItem
              icon={<AdminPanelSettingsIcon />}
              text="Admin"
              path="/admin"
              expanded={expanded}
            />
            <SidebarItem
              icon={<SecurityIcon />}
              text="Auth Logs"
              path="/auth-logs"
              expanded={expanded}
            />
            <SidebarItem
              icon={<AssessmentIcon />}
              text="Auth Dashboard"
              path="/auth-dashboard"
              expanded={expanded}
            />
            <SidebarItem
              icon={<SpeedIcon />}
              text="Performance"
              path="/performance"
              expanded={expanded}
            />
          </>
        )}
      </List>
      <Divider />
      <List sx={{ mt: "auto" }}>
        <SidebarItem
          icon={<PersonIcon />}
          text="Profile"
          path="/profile"
          expanded={expanded}
        />
        <SidebarItem
          icon={<LogoutIcon />}
          text="Logout"
          path="/logout"
          expanded={expanded}
          onClick={handleLogout}
        />
      </List>
    </Drawer>
  );
};

export default Sidebar;
